import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

import {
  Box,
  Typography,
  CardHeader,
  Button,
  CardActions,
  IconButton,
} from '@mui/material';

const ConnectorStepIcon = ({
  disabled,
  completed,
  active,
  stepNumber = 1,
}: {
  disabled: boolean;
  completed: boolean;
  active: boolean;
  stepNumber: number;
}) => {
  // TODO: maybe memoize this function
  const getIconColor = (
    disabled: boolean,
    completed: boolean,
    active: boolean,
  ) => {
    const iconColorMap = {
      disabled: '#0000001f',
      active: 'primary.main',
      completed: 'success.main',
    };

    if (completed) {
      return iconColorMap.completed;
    }

    if (disabled && !completed) {
      return iconColorMap.disabled;
    }

    if (!active) {
      return iconColorMap.disabled;
    }

    return iconColorMap.active;
  };
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: getIconColor(disabled, completed, active),
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {completed ? (
        <CheckIcon />
      ) : (
        <Typography
          sx={{ display: 'block' }}
          variant="caption"
          color="primary.contrastText"
        >
          {stepNumber}
        </Typography>
      )}
    </Box>
  );
};

const ConnectorCard = (props: {
  title: string;
  subheader?: string;
  active: boolean;
  disabled: boolean;
  completed: boolean;
  stepNumber: number;
  renderChildren?: (buttonsAreDisabled: boolean) => React.ReactNode;
  onNextClick?: () => void;
  onBackClick?: () => void;
  sx?: any;
}) => {
  const {
    title,
    subheader,
    active,
    completed,
    disabled = false,
    stepNumber,
    renderChildren,
    onNextClick,
    onBackClick,
    sx,
  } = props;
  const controlledCardProps = {
    ...(disabled && { variant: 'outlined' as 'outlined' | 'elevation' }),
    ...(active && { raised: true }),
    ...(!active && {
      raised: false,
      variant: 'outlined' as 'outlined' | 'elevation',
    }),
    ...(completed && { variant: undefined }),
  };
  const buttonsAreDisabled = disabled || completed;
  const showActions = active && !completed && !disabled;

  return (
    <Card
      {...controlledCardProps}
      sx={{
        maxWidth: 556,
        minHeight: 143,
        ...(buttonsAreDisabled && { pointerEvents: 'none' }),

        ...sx,
      }}
    >
      <CardHeader
        sx={{
          alignItems: 'flex-start',
          ...(disabled &&
            !completed && {
              '.MuiTypography-root': { color: 'text.disabled' },
            }),
          '& .MuiCardHeader-avatar': {
            paddingTop: 0.5,
          },
        }}
        disableTypography={true}
        avatar={
          <ConnectorStepIcon
            disabled={disabled}
            completed={completed}
            active={active}
            stepNumber={stepNumber}
          />
        }
        title={<Typography variant="h5">{title}</Typography>}
        subheader={
          subheader && (
            <Typography variant="body2" color="text.secondary">
              {subheader}
            </Typography>
          )
        }
      />
      <CardContent
        sx={{
          ...(disabled && {
            '.MuiTypography-root': { color: 'text.disabled' },
            '.field': { color: 'text.disabled' },
          }),
        }}
      >
        {renderChildren && renderChildren(buttonsAreDisabled)}
      </CardContent>
      {showActions && (
        <CardActions
          disableSpacing={true}
          sx={{
            justifyContent: stepNumber > 1 ? 'space-between' : 'flex-end',
            paddingTop: 0,
          }}
        >
          {stepNumber > 1 && onBackClick && (
            <Button
              onClick={() => {
                onBackClick?.();
              }}
              startIcon={<ArrowBackIcon />}
            >
              Previous
            </Button>
          )}
          {onNextClick && (
            <Button
              onClick={() => {
                onNextClick?.();
              }}
              sx={{ alignSelf: 'flex-end' }}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default ConnectorCard;
